import { FormikProps } from "formik/dist/types"
import clsx from "clsx"
import React, { forwardRef, ReactNode } from "react"
import { ObjectSchema } from "yup"
import useFormik from "./useFormik"
import InputMask from "react-input-mask"
import DatePicker, { registerLocale } from "react-datepicker"
import moment from "moment/moment"
import fr from "date-fns/locale/fr"
import { getIn } from "formik"

registerLocale("fr", fr)

// Composant interne pour gérer proprement le CustomInput avec Ref et Props de DatePicker
const MaskedInput = forwardRef(({ value, onClick, onChange, onBlur, placeholder, isError, autoFocus }: any, ref: any) => (
     <InputMask mask="99/99/9999" value={value} onChange={onChange} onClick={onClick} onBlur={onBlur}>
          {(inputProps: any) => (
               <input
                    {...inputProps}
                    ref={ref}
                    type="text"
                    inputMode={"numeric"}
                    placeholder={placeholder}
                    className={clsx("form-control bg-transparent", { "border-danger": isError })}
                    autoFocus={autoFocus}
               />
          )}
     </InputMask>
))

const MyDateDayMonthYearFormField = ({
     label,
     name,
     formikProps,
     placeholder,
     classNameLayout,
     classNameLabel,
     classNameField,
     autoFocus,
     validationSchema,
     footerRightSide,
     inputRightSideIcon,
     areWeekendsDisabled = true,
     minDate,
     maxDate,
     isClearable = true,
     isDisabled = false,
     defaultOpenMonth = { month: moment().month(), year: moment().year() },
}: IPropsModel) => {
     const isWeekday = (date: any) => {
          const day = date.getDay()
          return day !== 0 && day !== 6
     }
     const { isFieldOnError, getFieldErrorMessage, isFieldRequired } = useFormik()

     const fieldValue = formikProps ? getIn(formikProps.values, name) : null

     // Synchronisation manuelle si la saisie est complète (10 caractères) pour éviter le rollback
     const handleRawChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value
          if (value && value.length === 10) {
               const date = moment(value, "DD/MM/YYYY", true)
               if (date.isValid()) {
                    formikProps?.setFieldValue(name, date.format("YYYY-MM-DD"))
               }
          }
     }

     return (
          <div className={clsx("row", classNameLayout)}>
               <div className={clsx("col-12 d-flex align-items-center", classNameLabel)}>
                    {label && (
                         <label
                              className={clsx("fs-6 fw-bolder text-gray-900 d-flex align-items-center", {
                                   required: validationSchema && isFieldRequired(validationSchema, name),
                              })}
                         >
                              {label}
                         </label>
                    )}
               </div>
               <div className={clsx("col-12", classNameField, { "mt-1": label })}>
                    <div className={"input-group"}>
                         <DatePicker
                              dateFormat="dd/MM/yyyy"
                              locale="fr"
                              placeholderText={placeholder}
                              openToDate={moment().year(defaultOpenMonth.year).month(defaultOpenMonth.month).toDate()}
                              // Utilisation du composant MaskedInput qui propage correctement les événements
                              customInput={<MaskedInput placeholder={placeholder} isError={formikProps && isFieldOnError(formikProps, name)} autoFocus={autoFocus} />}
                              name={name}
                              minDate={minDate ? moment(minDate).toDate() : null}
                              maxDate={maxDate ? moment(maxDate).toDate() : null}
                              isClearable={isClearable}
                              disabled={isDisabled}
                              selected={fieldValue ? moment(fieldValue).toDate() : null}
                              // selected={formikProps?.values[name] ? moment(formikProps.values[name]).toDate() : null}
                              // Force la mise à jour Formik même sur saisie brute
                              onChangeRaw={handleRawChange}
                              filterDate={areWeekendsDisabled ? isWeekday : undefined}
                              onChange={(val: any) => {
                                   formikProps?.setFieldTouched(name, true)
                                   if (val) {
                                        const d = moment(val).format("YYYY-MM-DD")
                                        formikProps?.setFieldValue(name, d)
                                   } else {
                                        formikProps?.setFieldValue(name, null)
                                   }
                              }}
                         />

                         {inputRightSideIcon && (
                              <span className={clsx("input-group-text", { "border-danger": formikProps && isFieldOnError(formikProps, name) })}>
                                   <i className={`fas fa-${inputRightSideIcon}`} />
                              </span>
                         )}
                    </div>
                    {(formikProps || footerRightSide) && (
                         <div className="row">
                              <div className="col text-start">
                                   {formikProps && isFieldOnError(formikProps, name) && <div className="text-danger">{getFieldErrorMessage(formikProps, name)}</div>}
                              </div>
                              {footerRightSide && <div className="col text-end">{footerRightSide}</div>}
                         </div>
                    )}
               </div>
          </div>
     )
}

interface IPropsModel {
     label?: ReactNode
     name: string
     formikProps: FormikProps<any> | null
     placeholder: string
     type?: "text" | "number" | "email" | "password"
     inputMode?: "text" | "numeric" | "tel" | "email"
     autoComplete?: "on" | "off"
     classNameLayout?: string
     classNameLabel?: string
     classNameField?: string
     autoFocus?: boolean
     validationSchema?: ObjectSchema<any>
     footerRightSide?: ReactNode
     inputRightSideIcon?: string
     areWeekendsDisabled?: boolean
     minDate?: string
     maxDate?: string
     isClearable?: boolean
     isDisabled?: boolean
     defaultOpenMonth?: { month: number; year: number }
}

export default MyDateDayMonthYearFormField
